import { useState, useEffect, useCallback, useRef } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import DashboardLayout from "./components/DashboardLayout";
import { TicketCheck, Globe, Gauge, Zap, PlusCircle } from "lucide-react";
import websiteService from "../../services/websiteService";
import websitePageSpeedService from "../../services/WebsitePageSpeedService";
import ticketService from "../../services/ticketService";
import pageService from "../../services/pageService";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "../../context/AuthContext";
import SyncLoadingOverlay from "../../components/SyncLoadingOverlay";
import authService from "../../services/authService";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

// Calculate performance score based on Lighthouse methodology
// Weights from https://developer.chrome.com/docs/lighthouse/performance/performance-scoring/
const calculatePerformanceScore = (metrics) => {
  if (!metrics || !metrics.first_paint || !metrics.load_time) return null;
  
  // Define metric weights as per Lighthouse
  const weights = {
    FCP: 0.10,  // First Contentful Paint
    LCP: 0.25,  // Largest Contentful Paint (using first_paint as a proxy)
    TBT: 0.30,  // Total Blocking Time
    CLS: 0.15,  // Cumulative Layout Shift (assuming 0)
    SI: 0.10,   // Speed Index
    TTI: 0.10   // Time to Interactive (using load_time as a proxy)
  };
  
  // Calculate the scores for each metric using the Lighthouse scoring approach
  const fcpScore = calculateFCPScore(metrics.first_paint);
  const lcpScore = calculateLCPScore(metrics.first_paint); // Using first_paint as proxy for LCP
  const tbtScore = calculateTBTScore(metrics.cpu_load_percent); // Using CPU load as a proxy for TBT
  const clsScore = 1.0; // Assuming CLS is great (1.0)
  const siScore = calculateSIScore(metrics.load_time);
  const ttiScore = calculateTTIScore(metrics.load_time);
  
  // Calculate weighted average
  const weightedScore = 
    weights.FCP * fcpScore +
    weights.LCP * lcpScore +
    weights.TBT * tbtScore +
    weights.CLS * clsScore +
    weights.SI * siScore +
    weights.TTI * ttiScore;
  
  // Return a score between 0-100
  return Math.round(weightedScore * 100);
};

// Scoring functions based on Lighthouse curves
const calculateFCPScore = (fcp) => {
  if (fcp <= 1800) return 1.0;
  if (fcp <= 3000) return 0.9 - (fcp - 1800) / 12000;
  return Math.max(0, 0.9 - (fcp - 3000) / 6000);
};

const calculateLCPScore = (lcp) => {
  if (lcp <= 2500) return 1.0;
  if (lcp <= 4000) return 0.9 - (lcp - 2500) / 15000;
  return Math.max(0, 0.9 - (lcp - 4000) / 10000);
};

const calculateTBTScore = (cpu) => {
  if (cpu <= 30) return 1.0;
  if (cpu <= 50) return 0.9 - (cpu - 30) / 200;
  return Math.max(0, 0.9 - (cpu - 50) / 100);
};

const calculateSIScore = (loadTime) => {
  if (loadTime <= 3000) return 1.0;
  if (loadTime <= 5000) return 0.9 - (loadTime - 3000) / 20000;
  return Math.max(0, 0.9 - (loadTime - 5000) / 15000);
};

const calculateTTIScore = (loadTime) => {
  if (loadTime <= 3500) return 1.0;
  if (loadTime <= 5500) return 0.9 - (loadTime - 3500) / 20000;
  return Math.max(0, 0.9 - (loadTime - 5500) / 15000);
};

const Dashboard = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isExactDashboardPath = location.pathname === '/dashboard';
  const driverRef = useRef(null);
  
  // State for API data
  const [websiteStats, setWebsiteStats] = useState({
    totalWebsites: 0,
    totalPages: 0,
    averageDesktopSpeed: 0,
    averageMobileSpeed: 0,
    openTickets: 0,
  });
  const [websites, setWebsites] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataFullyLoaded, setDataFullyLoaded] = useState(false);
  const [dataFetchInitiated, setDataFetchInitiated] = useState(false);
  const [profile, setProfile] = useState(null);
  
  // Track the actual raw data from API responses
  const [rawWebsitesData, setRawWebsitesData] = useState([]);
  const [rawTicketsData, setRawTicketsData] = useState([]);
  
  // State for onboarding tour
  const [shouldShowTour, setShouldShowTour] = useState(false);
  const [tourAllowed, setTourAllowed] = useState(false);
  const [elementsReady, setElementsReady] = useState(false);
  
  // Parse and fix any FreeScout data that's in string format
  const parseTicketFreescoutData = (ticketsData) => {
    return ticketsData.map(ticket => {
      if (ticket.freescout_data && typeof ticket.freescout_data === 'string') {
        try {
          return {
            ...ticket,
            freescout_data: JSON.parse(ticket.freescout_data)
          };
        } catch (error) {
          console.error("Error parsing freescout_data in dashboard:", error);
          // Return the original ticket if parsing fails
          return ticket;
        }
      }
      return ticket;
    });
  };
  
  // Check if the tour should be shown
  const checkIfShouldShowTour = useCallback(() => {
    // Only show the tour if we're on the exact dashboard path
    if (!isExactDashboardPath) return false;
    
    // Only check once all data is fully loaded
    if (!dataFullyLoaded) return false;
    
    // Check if user has no websites or tickets based on the raw API data
    const hasNoWebsites = rawWebsitesData.length === 0;
    const hasNoTickets = rawTicketsData.length === 0;
    
    console.log(`Tour check - hasNoWebsites: ${hasNoWebsites}, hasNoTickets: ${hasNoTickets}`);
    console.log(`Raw websites count: ${rawWebsitesData.length}, Raw tickets count: ${rawTicketsData.length}`);
    
    if (!hasNoWebsites && !hasNoTickets) {
      // User has websites and tickets, no need to show tour
      return false;
    }
    
    // Check localStorage for the last time we showed the tour
    const lastTourShown = localStorage.getItem('lastDashboardTourShown');
    
    if (lastTourShown) {
      // Calculate if 24 hours have passed since last shown
      const lastShownDate = new Date(parseInt(lastTourShown, 10));
      const now = new Date();
      const hoursSinceLastShown = (now - lastShownDate) / (1000 * 60 * 60);
      
      // If less than 24 hours have passed, don't show the tour
      if (hoursSinceLastShown < 24) {
        console.log(`Tour was shown recently (${hoursSinceLastShown.toFixed(2)} hours ago), skipping`);
        return false;
      }
    }
    
    // If we've reached here, we should show the tour
    console.log("All conditions met for showing tour");
    return true;
  }, [isExactDashboardPath, dataFullyLoaded, rawWebsitesData, rawTicketsData]);
  
  // Initialize the tour when needed
  const initTour = useCallback(() => {
    // Set the current timestamp in localStorage
    localStorage.setItem('lastDashboardTourShown', Date.now().toString());
    
    // Configure the driver
    const driverObj = driver({
      animate: true,
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: [
        {
          element: '#add-website-btn',
          popover: {
            title: 'Start by adding a website',
            description: 'Add your first website to analyze its performance and get personalized recommendations.',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '#create-ticket-btn',
          popover: {
            title: 'Create a support ticket',
            description: 'It\'s free to create a ticket for diagnosis! Our team will assess the issue and let you know how many support credits are needed to resolve it. You can purchase credits as needed or through a subscription plan.',
            side: 'bottom',
            align: 'start'
          }
        }
      ]
    });
    
    // Store the driver instance in ref
    driverRef.current = driverObj;
    
    // Start the tour
    driverObj.drive();
    
    // Return a cleanup function
    return () => {
      driverObj.destroy();
    };
  }, []);
  
  // Determine if tour should be allowed to show
  useEffect(() => {
    // Only allow the tour when both data is loaded and DOM elements are verified to exist
    if (dataFullyLoaded && elementsReady && !loading) {
      const shouldShow = checkIfShouldShowTour();
      setTourAllowed(shouldShow);
    } else {
      setTourAllowed(false);
    }
  }, [dataFullyLoaded, elementsReady, loading, checkIfShouldShowTour]);

  // Only trigger the tour when all conditions are met and enough time has passed for stable rendering
  useEffect(() => {
    if (!tourAllowed) return;
    
    // Wait a bit longer to ensure UI is fully stable before showing tour
    const finalTourTimeout = setTimeout(() => {
      console.log("Starting tour - all conditions met");
      setShouldShowTour(true);
    }, 1500);
    
    return () => clearTimeout(finalTourTimeout);
  }, [tourAllowed]);
  
  // Start the tour if shouldShowTour becomes true
  useEffect(() => {
    if (shouldShowTour) {
      const cleanup = initTour();
      return cleanup;
    }
  }, [shouldShowTour, initTour]);

  // Check if DOM elements required for the tour are present
  useEffect(() => {
    if (!dataFullyLoaded || loading) return;

    // Use a timeout to ensure DOM has fully rendered
    const checkElementsTimeout = setTimeout(() => {
      const addWebsiteBtn = document.getElementById('add-website-btn');
      const createTicketBtn = document.getElementById('create-ticket-btn');
      const websitesSection = document.getElementById('websites-section');
      const ticketsSection = document.getElementById('tickets-section');
      
      // Only set elements ready if all required elements exist in the DOM
      if (addWebsiteBtn && createTicketBtn && websitesSection && ticketsSection) {
        console.log("Tour elements are ready in the DOM");
        setElementsReady(true);
      } else {
        console.log("Tour elements not all found in DOM yet");
        setElementsReady(false);
      }
    }, 1000); // Give extra time for DOM to fully render
    
    return () => clearTimeout(checkElementsTimeout);
  }, [dataFullyLoaded, loading]);

  // Get the display name from profile, fallback to user context
  const displayName = profile?.given_name || user?.name || 'User';

  // Fetch all dashboard data including websites, pages and tickets
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setDataFullyLoaded(false); // Reset the fully loaded state at the start of data fetching
      
      // Get user profile information
      const userResponse = await authService.getCurrentUser();
      if (userResponse?.success) {
        setProfile(userResponse.user);
      }
      
      // Fetch all websites for this user
      const websitesResponse = await websiteService.getUserWebsites();
      
      if (!websitesResponse || !websitesResponse.success) {
        throw new Error("Failed to fetch websites");
      }
      
      const userWebsites = websitesResponse.websites || [];
      // Store raw websites data for tour condition checking
      setRawWebsitesData(userWebsites);
      console.log(`Found ${userWebsites.length} websites in total`);
      
      // Only display first 3 websites in the UI
      setWebsites(userWebsites.slice(0, 3));
      
      // Fetch recent tickets
      const ticketsResponse = await ticketService.getAllTickets({ limit: 2, offset: 0 });
      
      if (!ticketsResponse || !ticketsResponse.success) {
        throw new Error("Failed to fetch tickets");
      }
      
      // Store all tickets for tour condition checking
      const allTickets = ticketsResponse.tickets || [];
      setRawTicketsData(allTickets);
      console.log(`Found ${allTickets.length} tickets in total`);
      
      // Parse any string-based freescout_data to JSON objects
      const parsedTickets = parseTicketFreescoutData(allTickets);
      // Only display first 2 tickets in the UI
      setTickets(parsedTickets.slice(0, 2));
      
      // Get ticket stats for open tickets count
      const ticketStats = await ticketService.getTicketStats();
      const openTicketsCount = ticketStats?.success ? (ticketStats.stats.by_status.open || 0) : 0;
      
      // Get PageSpeed data and calculate website performance metrics
      let totalPagesCount = 0;
      let totalDesktopScores = 0;
      let totalMobileScores = 0;
      let websitesWithDesktopScores = 0;
      let websitesWithMobileScores = 0;
      
      // Enhanced websites with performance data
      const enhancedWebsites = [];
      
      // Process each website to get PageSpeed data
      for (const website of userWebsites) {
        if (!website || !website.id || !website.root_url) {
          continue;
        }
        
        // Get page count
        try {
          const pagesResponse = await pageService.getWebsitePages(website.id, { limit: 100 });
          if (pagesResponse && pagesResponse.success) {
            const pageCount = pagesResponse.total || pagesResponse.pages.length || 0;
            totalPagesCount += pageCount;
            
            // Enhance website with page count
            website.page_count = pageCount;
          }
        } catch (error) {
          console.error(`Error fetching pages for website ${website.id}:`, error);
        }
        
        // Get PageSpeed data for this website
        try {
          // Try to get the latest desktop PageSpeed data
          const desktopResponse = await websitePageSpeedService.getWebsitePageSpeedById(website.id, { 
            is_mobile: false,
            limit: 1
          });
          
          // Extract PageSpeed analysis data if available for desktop
          let desktopPageSpeedData = null;
          if (desktopResponse?.success && desktopResponse?.analyses?.length > 0) {
            desktopPageSpeedData = desktopResponse.analyses[0];
          } else if (desktopResponse?.success && desktopResponse?.pageSpeeds?.length > 0) {
            desktopPageSpeedData = desktopResponse.pageSpeeds[0];
          }
          
          // Try to get the latest mobile PageSpeed data
          const mobileResponse = await websitePageSpeedService.getWebsitePageSpeedById(website.id, { 
            is_mobile: true,
            limit: 1
          });
          
          // Extract PageSpeed analysis data if available for mobile
          let mobilePageSpeedData = null;
          if (mobileResponse?.success && mobileResponse?.analyses?.length > 0) {
            mobilePageSpeedData = mobileResponse.analyses[0];
          } else if (mobileResponse?.success && mobileResponse?.pageSpeeds?.length > 0) {
            mobilePageSpeedData = mobileResponse.pageSpeeds[0];
          }
          
          // Initialize enhanced website data with performance scores
          const enhancedWebsite = { ...website };
          
          // If desktop PageSpeed data was found, process it
          if (desktopPageSpeedData) {
            console.log("Desktop PageSpeed data:", desktopPageSpeedData);
            
            // Initialize variables to store extracted data
            let desktopPerformanceScore = null;
            let desktopLoadTime = null;
            let desktopRawMetrics = null;
            let desktopRawData = null;
            
            // Try to parse the raw_metrics to get performance data
            if (desktopPageSpeedData.raw_metrics) {
              try {
                desktopRawMetrics = typeof desktopPageSpeedData.raw_metrics === 'string' 
                  ? JSON.parse(desktopPageSpeedData.raw_metrics) 
                  : desktopPageSpeedData.raw_metrics;
                
                // Get load time from raw metrics
                desktopLoadTime = desktopRawMetrics.load_time || desktopRawMetrics.page_load_time || null;
                
                // If raw_data is also available, try to extract Lighthouse performance score
                if (desktopPageSpeedData.raw_data) {
                  try {
                    const parsedData = typeof desktopPageSpeedData.raw_data === 'string'
                      ? JSON.parse(desktopPageSpeedData.raw_data)
                      : desktopPageSpeedData.raw_data;
                      
                    desktopRawData = parsedData.raw_data 
                      ? (typeof parsedData.raw_data === 'string' ? JSON.parse(parsedData.raw_data) : parsedData.raw_data) 
                      : parsedData;
                    
                    // Use our custom calculation instead of the Lighthouse score
                    // even if Lighthouse performance score is available
                  } catch (parseError) {
                    console.log("Error parsing desktop raw_data:", parseError);
                  }
                }
                
                // Calculate our own performance score using raw metrics
                if (desktopRawMetrics && desktopRawMetrics.first_paint && desktopRawMetrics.load_time) {
                  desktopPerformanceScore = calculatePerformanceScore(desktopRawMetrics);
                }
                
                // Add to totals and update the website object
                if (desktopPerformanceScore !== null) {
                  totalDesktopScores += desktopPerformanceScore;
                  websitesWithDesktopScores++;
                  
                  enhancedWebsite.health_score = desktopPerformanceScore;
                  enhancedWebsite.page_load_time = desktopLoadTime;
                  enhancedWebsite.raw_metrics = desktopRawMetrics;
                  enhancedWebsite.raw_data = desktopRawData;
                  enhancedWebsite.last_analyzed_at = desktopPageSpeedData.created_at || desktopPageSpeedData.analyzed_at;
                }
              } catch (parseError) {
                console.log("Error parsing desktop raw_metrics:", parseError);
              }
            }
          }
          
          // If mobile PageSpeed data was found, process it
          if (mobilePageSpeedData) {
            console.log("Mobile PageSpeed data:", mobilePageSpeedData);
            
            // Initialize variables for mobile data
            let mobilePerformanceScore = null;
            let mobileLoadTime = null;
            let mobileRawMetrics = null;
            let mobileRawData = null;
            
            // Parse mobile raw_metrics
            if (mobilePageSpeedData.raw_metrics) {
              try {
                mobileRawMetrics = typeof mobilePageSpeedData.raw_metrics === 'string' 
                  ? JSON.parse(mobilePageSpeedData.raw_metrics) 
                  : mobilePageSpeedData.raw_metrics;
                
                mobileLoadTime = mobileRawMetrics.load_time || mobileRawMetrics.page_load_time || null;
                
                // Parse mobile raw_data if available
                if (mobilePageSpeedData.raw_data) {
                  try {
                    const parsedData = typeof mobilePageSpeedData.raw_data === 'string'
                      ? JSON.parse(mobilePageSpeedData.raw_data)
                      : mobilePageSpeedData.raw_data;
                      
                    mobileRawData = parsedData.raw_data 
                      ? (typeof parsedData.raw_data === 'string' ? JSON.parse(parsedData.raw_data) : parsedData.raw_data) 
                      : parsedData;
                    
                  } catch (parseError) {
                    console.log("Error parsing mobile raw_data:", parseError);
                  }
                }
                
                // Calculate mobile performance score
                if (mobileRawMetrics && mobileRawMetrics.first_paint && mobileRawMetrics.load_time) {
                  mobilePerformanceScore = calculatePerformanceScore(mobileRawMetrics);
                }
                
                // Add to totals and update the website object
                if (mobilePerformanceScore !== null) {
                  totalMobileScores += mobilePerformanceScore;
                  websitesWithMobileScores++;
                  
                  // Update website with mobile data
                  enhancedWebsite.mobile_health_score = mobilePerformanceScore;
                  enhancedWebsite.mobile_page_load_time = mobileLoadTime;
                  enhancedWebsite.mobile_raw_metrics = mobileRawMetrics;
                  enhancedWebsite.mobile_raw_data = mobileRawData;
                  
                  // If desktop data is not available, use mobile data for last scan time
                  if (!enhancedWebsite.last_analyzed_at) {
                    enhancedWebsite.last_analyzed_at = mobilePageSpeedData.created_at || mobilePageSpeedData.analyzed_at || website.last_scanned_at;
                  }
                }
              } catch (parseError) {
                console.log("Error parsing mobile raw_metrics:", parseError);
              }
            }
          }
          
          // Add enhanced website to array
          enhancedWebsites.push(enhancedWebsite);
        } catch (error) {
          console.error(`Error fetching PageSpeed data for website ${website.id}:`, error);
          enhancedWebsites.push(website); // Add website even without performance data
        }
      }
      
      // Update the websites with enhanced data
      if (enhancedWebsites.length > 0) {
        setWebsites(enhancedWebsites.slice(0, 3));
      }
      
      // Calculate the average performance scores separately
      let averageDesktopSpeed = 0;
      let averageMobileSpeed = 0;
      
      if (websitesWithDesktopScores > 0) {
        averageDesktopSpeed = Math.round(totalDesktopScores / websitesWithDesktopScores);
      }
      
      if (websitesWithMobileScores > 0) {
        averageMobileSpeed = Math.round(totalMobileScores / websitesWithMobileScores);
      }
      
      // Update stats
      setWebsiteStats({
        totalWebsites: userWebsites.length,
        totalPages: totalPagesCount,
        averageDesktopSpeed,
        averageMobileSpeed,
        openTickets: openTicketsCount
      });
      
      // Mark data as fully loaded only after all operations are complete
      setDataFullyLoaded(true);
      
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      // Even in case of error, mark data as loaded so we can show the tour if needed
      setDataFullyLoaded(true);
    } finally {
      setLoading(false);
    }
  }, []);  // Empty dependency array as this doesn't depend on any props or state

  // Fetch data on component mount
  useEffect(() => {
    if (isExactDashboardPath && !dataFetchInitiated) {
      // Set flag to indicate data fetching has begun to prevent duplicate requests
      setDataFetchInitiated(true);
      fetchDashboardData();
    }
  }, [isExactDashboardPath, fetchDashboardData, dataFetchInitiated]);

  // Format date to relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (err) {
      console.error("Error formatting date:", err);
      return "Invalid date";
    }
  };

  return (
    <DashboardLayout>
      {/* If we're at exact /dashboard path, show the dashboard homepage */}
      {isExactDashboardPath ? (
        <div className="container p-4 mx-auto">
          {loading && <SyncLoadingOverlay message="Loading dashboard data..." />}

          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Welcome back, {displayName}!</h1>
              <p className="text-gray-500">Here's an overview of your websites</p>
            </div>
            <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
              <Link 
                to="/dashboard/tickets/new" 
                id="create-ticket-btn"
                className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <PlusCircle className="h-4 w-4" />
                New Support Ticket
              </Link>
              <Link 
                to="/dashboard/websites/new"
                id="add-website-btn"
                className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                <PlusCircle className="h-4 w-4" />
                Add Website
              </Link>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Websites</p>
                  <h3 className="text-2xl font-bold">{websiteStats.totalWebsites}</h3>
                </div>
                <div className="rounded-full bg-blue-100 p-3">
                  <Globe className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Pages</p>
                  <h3 className="text-2xl font-bold">{websiteStats.totalPages}</h3>
                </div>
                <div className="rounded-full bg-green-100 p-3">
                  <Gauge className="h-5 w-5 text-green-500" />
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-gray-500">Average Speed</p>
                </div>
                <div className="rounded-full bg-yellow-100 p-3">
                  <Zap className="h-5 w-5 text-yellow-500" />
                </div>
              </div>
              {/* Desktop Speed */}
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Desktop:</span>
                  <span className="text-lg font-bold">{websiteStats.averageDesktopSpeed || 'N/A'}</span>
                </div>
                {websiteStats.averageDesktopSpeed > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className={`h-2 rounded-full ${
                        websiteStats.averageDesktopSpeed >= 90 ? "bg-green-500" :
                        websiteStats.averageDesktopSpeed >= 70 ? "bg-yellow-500" : "bg-red-500"
                      }`}
                      style={{ width: `${websiteStats.averageDesktopSpeed}%` }}
                    ></div>
                  </div>
                )}
              </div>
              {/* Mobile Speed */}
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Mobile:</span>
                  <span className="text-lg font-bold">{websiteStats.averageMobileSpeed || 'N/A'}</span>
                </div>
                {websiteStats.averageMobileSpeed > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className={`h-2 rounded-full ${
                        websiteStats.averageMobileSpeed >= 90 ? "bg-green-500" :
                        websiteStats.averageMobileSpeed >= 70 ? "bg-yellow-500" : "bg-red-500"
                      }`}
                      style={{ width: `${websiteStats.averageMobileSpeed}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Open Tickets</p>
                  <h3 className="text-2xl font-bold">{websiteStats.openTickets}</h3>
                </div>
                <div className="rounded-full bg-red-100 p-3">
                  <TicketCheck className="h-5 w-5 text-red-500" />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Websites List */}
            <div className="lg:col-span-2" id="websites-section">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div>
                    <h2 className="text-lg font-semibold">Your Websites</h2>
                    <p className="text-sm text-gray-500">Manage and monitor your websites</p>
                  </div>
                  <Link 
                    to="/dashboard/websites" 
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View All
                  </Link>
                </div>
                <div className="overflow-x-auto">
                  {websites.length === 0 && !loading ? (
                    <div className="p-8 text-center text-gray-500">
                      No websites found. Add your first website to get started.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Website</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Platform</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Performance</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-500">Last Checked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {websites.map((website) => (
                          <tr key={website.id} className="border-t border-gray-200">
                            <td className="px-6 py-4">
                              <Link to={`/dashboard/websites/${website.id}`} className="text-blue-600 hover:underline font-medium">
                                {website.name || website.root_url}
                              </Link>
                              <div className="text-xs text-gray-500 mt-1">
                                {website.root_url}
                              </div>
                            </td>
                            <td className="px-6 py-4">{website.platform}</td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col">
                                {/* Desktop Performance */}
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="text-xs text-gray-500 w-16">Desktop:</div>
                                  <div className="flex items-center gap-2 flex-1">
                                    <div className="h-2 w-20 rounded-full bg-gray-200">
                                      <div
                                        className={`h-2 rounded-full ${
                                          website.health_score >= 90
                                            ? "bg-emerald-500" // Excellent (90-100)
                                            : website.health_score >= 80
                                            ? "bg-green-500" // Good (80-89)
                                            : website.health_score >= 70
                                            ? "bg-lime-500" // Fair (70-79)
                                            : website.health_score >= 50
                                            ? "bg-yellow-500" // Needs Improvement (50-69)
                                            : website.health_score >= 30
                                            ? "bg-orange-500" // Poor (30-49)
                                            : "bg-red-500" // Very Poor (0-29)
                                        }`}
                                        style={{ width: `${website.health_score || 0}%` }}
                                      ></div>
                                    </div>
                                    <span className={`${
                                      website.health_score >= 90
                                        ? "text-emerald-700"
                                        : website.health_score >= 80
                                        ? "text-green-700" 
                                        : website.health_score >= 70
                                        ? "text-lime-700"
                                        : website.health_score >= 50
                                        ? "text-yellow-700"
                                        : website.health_score >= 30
                                        ? "text-orange-700"
                                        : "text-red-700"
                                    }`}>{website.health_score || 0}</span>
                                  </div>
                                </div>

                                {/* Mobile Performance */}
                                <div className="flex items-center gap-2">
                                  <div className="text-xs text-gray-500 w-16">Mobile:</div>
                                  <div className="flex items-center gap-2 flex-1">
                                    <div className="h-2 w-20 rounded-full bg-gray-200">
                                      <div
                                        className={`h-2 rounded-full ${
                                          website.mobile_health_score >= 90
                                            ? "bg-emerald-500" // Excellent (90-100)
                                            : website.mobile_health_score >= 80
                                            ? "bg-green-500" // Good (80-89)
                                            : website.mobile_health_score >= 70
                                            ? "bg-lime-500" // Fair (70-79)
                                            : website.mobile_health_score >= 50
                                            ? "bg-yellow-500" // Needs Improvement (50-69)
                                            : website.mobile_health_score >= 30
                                            ? "bg-orange-500" // Poor (30-49)
                                            : "bg-red-500" // Very Poor (0-29)
                                        }`}
                                        style={{ width: `${website.mobile_health_score || 0}%` }}
                                      ></div>
                                    </div>
                                    <span className={`${
                                      website.mobile_health_score >= 90
                                        ? "text-emerald-700"
                                        : website.mobile_health_score >= 80
                                        ? "text-green-700" 
                                        : website.mobile_health_score >= 70
                                        ? "text-lime-700"
                                        : website.mobile_health_score >= 50
                                        ? "text-yellow-700"
                                        : website.mobile_health_score >= 30
                                        ? "text-orange-700"
                                        : "text-red-700"
                                    }`}>{website.mobile_health_score || 0}</span>
                                  </div>
                                </div>

                                {/* Load time info (if available) */}
                                {(website.page_load_time || website.mobile_page_load_time) && (
                                  <div className="text-xs text-gray-500 mt-2">
                                    {website.page_load_time && (
                                      <div>Desktop: {(website.page_load_time / 1000).toFixed(1)}s</div>
                                    )}
                                    {website.mobile_page_load_time && (
                                      <div>Mobile: {(website.mobile_page_load_time / 1000).toFixed(1)}s</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-500">
                              {website.last_analyzed_at ? formatRelativeTime(website.last_analyzed_at) : "Never"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            {/* Support Tickets */}
            <div id="tickets-section">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div>
                    <h2 className="text-lg font-semibold">Recent Tickets</h2>
                    <p className="text-sm text-gray-500">Your support requests</p>
                  </div>
                  <Link 
                    to="/dashboard/tickets" 
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View All
                  </Link>
                </div>
                <div className="p-4 space-y-4">
                  {tickets.length === 0 && !loading ? (
                    <div className="p-4 text-center text-gray-500">
                      No tickets found. Create a support ticket to get help with your website.
                    </div>
                  ) : (
                    tickets.map((ticket) => (
                      <div key={ticket.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <Link to={`/dashboard/tickets/${ticket.id}`} className="font-medium text-blue-600 hover:underline">
                              {ticket.subject}
                            </Link>
                            <p className="text-sm text-gray-500">{ticket.website?.name || "via Email"}</p>
                          </div>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              ticket.status === "Open" || ticket.status === "active"
                                ? "bg-blue-100 text-blue-800"
                                : ticket.status === "In Progress" || ticket.status === "pending"
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {ticket.status === "active" ? "Open" : 
                            ticket.status === "pending" ? "In Progress" : 
                            ticket.status || "Unknown"}
                          </span>
                        </div>
                        <div className="flex justify-between mt-3 text-xs text-gray-500">
                          <span>Created: {formatRelativeTime(ticket.created_at)}</span>
                          <span
                            className={`font-medium ${
                              ticket.priority === "High"
                                ? "text-red-600"
                                : ticket.priority === "Medium"
                                  ? "text-yellow-600"
                                  : "text-green-600"
                            }`}
                          >
                            {ticket.priority || "Normal"} Priority
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Render child routes when not on the exact dashboard path */
        <Outlet />
      )}
    </DashboardLayout>
  );
};

export default Dashboard;